import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Plus, Trash2, PowerOff, Power, KeyRound, Users, Eye, UserCheck, X, Loader2, Search, ShieldCheck } from "lucide-react";
import { fmtDate } from "@/lib/date";
import { useAuth } from "@/lib/auth";

import PromoteLeaderDialog from "@/components/PromoteLeaderDialog";
import PboLinkPanel from "@/components/PboLinkPanel";
import ContentSyncPanel from "@/components/ContentSyncPanel";
import BackupPanel from "@/components/BackupPanel";

const ROLES = ["admin", "oversight", "platinum", "team_leader", "leader", "partner"];

export default function Admin() {
  const { startViewAs, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [pending, setPending] = useState([]);
  const [ref, setRef] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null); // user or null
  const [pwUser, setPwUser] = useState(null);
  const [pwVal, setPwVal] = useState("");
  const [confirmWipe, setConfirmWipe] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmReject, setConfirmReject] = useState(null);
  const [approving, setApproving] = useState(null);   // pending user under review
  const [approveTeam, setApproveTeam] = useState("");
  const [approveLeader, setApproveLeader] = useState("");
  const [filter, setFilter] = useState({ role: "__all__", team: "__all__" });
  const [q, setQ] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [searchHits, setSearchHits] = useState(null); // server-side search results (null = no search)
  const [promoting, setPromoting] = useState(null); // target user for Promote-to-Leader dialog

  const load = async () => {
    setLoading(true);
    const [uRes, rRes, pRes] = await Promise.allSettled([
      api.get("/users"),
      api.get("/reference"),
      api.get("/users/pending"),
    ]);
    if (uRes.status === "fulfilled") setUsers(uRes.value.data);
    else toast.error(uRes.reason?.response?.data?.detail || "Could not load users");
    if (rRes.status === "fulfilled") setRef(rRes.value.data);
    else toast.error("Could not load reference");
    if (pRes.status === "fulfilled") setPending(pRes.value.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const term = q.trim();
    if (!term) { setSearchHits(null); return undefined; }
    const t = setTimeout(() => {
      api.get("/users", { params: { q: term } }).then((r) => setSearchHits(r.data)).catch(() => setSearchHits([]));
    }, 250);
    return () => clearTimeout(t);
  }, [q, users]);

  const togglePlatinumAdmin = async (u) => {
    const on = !u.platinum_admin;
    if (!window.confirm(`${on ? "Give" : "Remove"} Platinum Admin access ${on ? "to" : "from"} ${u.name}?${on ? " They will be able to approve, add, move, promote and archive people in their own downline." : ""}`)) return;
    try { await api.post(`/users/${u.id}/platinum-admin`, { enabled: on }); toast.success(on ? "Platinum Admin granted" : "Platinum Admin removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
  };

  const filtered = useMemo(() => {
    let list = searchHits ?? users;
    if (!showArchived) list = list.filter((u) => !u.archived);
    if (filter.role !== "__all__") list = list.filter((u) => u.role === filter.role);
    if (filter.team !== "__all__") list = list.filter((u) => u.upline_team === filter.team);
    return list;
  }, [users, filter, searchHits, showArchived]);
  const archivedCount = useMemo(() => users.filter((u) => u.archived).length, [users]);

  const userMap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  const leaderOptions = useMemo(() =>
    users.filter((u) => ["admin", "platinum", "team_leader", "leader"].includes(u.role) && u.approved
      && !u.archived && !u.is_placeholder && u.active !== false && !u.merged_into),
  [users]);

  const changePw = async () => {
    if (!pwVal || pwVal.length < 6) { toast.error("Min 6 chars"); return; }
    try {
      await api.patch(`/users/${pwUser.id}`, { password: pwVal });
      toast.success("Password updated"); setPwUser(null); setPwVal("");
    } catch { toast.error("Failed"); }
  };

  const toggle = async (u) => {
    try { await api.post(`/users/${u.id}/toggle-active`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/users/${confirmDelete.id}`);
      toast.success("Deleted"); setConfirmDelete(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const wipeDemo = async () => {
    try {
      const { data } = await api.delete("/pbos/demo/all");
      toast.success(`Removed ${data.deleted} demo PBOs`); setConfirmWipe(false);
    } catch { toast.error("Failed"); }
  };

  const openApprove = (u) => {
    setApproving(u);
    setApproveTeam(u.upline_team || "");
    setApproveLeader(u.nearest_upline_leader_id || "");
  };
  const doApprove = async () => {
    if (!approveTeam) { toast.error("Team is required to approve"); return; }
    try {
      await api.post(`/users/${approving.id}/approve`, {
        upline_team: approveTeam,
        nearest_upline_leader_id: approveLeader || null,
      });
      toast.success(`${approving.name} approved`);
      setApproving(null); setApproveTeam(""); setApproveLeader("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const doReject = async () => {
    try {
      await api.post(`/users/${confirmReject.id}/reject`);
      toast.success(`${confirmReject.name} declined`);
      setConfirmReject(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const approveLeadersFiltered = useMemo(() => {
    if (!approveTeam) return leaderOptions;
    return leaderOptions.filter((l) => l.upline_team === approveTeam || l.role === "admin");
  }, [approveTeam, leaderOptions]);

  const doArchive = async (u, reason) => {
    try {
      await api.post(`/users/${u.id}/archive`, { confirm: true, reason: reason || "" });
      toast.success(`${u.name} archived`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const doReactivate = async (u) => {
    try {
      await api.post(`/users/${u.id}/reactivate`);
      toast.success(`${u.name} reactivated`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const canArchive = (u) => {
    if (u.role !== "partner") return false;
    const r = currentUser?.role;
    if (r === "admin") return true;
    // Platinum/team_leader → UI best-effort; server authoritatively rejects
    // if the target is outside their downline.
    return ["platinum", "team_leader"].includes(r);
  };

  return (
    <div className="space-y-6" data-testid="admin-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.24em] text-[#C9A961] font-semibold">Administration</div>
          <h1 className="font-serif text-3xl sm:text-4xl text-[#0A1F44] mt-1">Admin</h1>
          <p className="text-sm text-slate-500 mt-1">Approve users, edit roles, and manage the leader hierarchy.</p>
        </div>
        <Button data-testid="add-user-btn" onClick={() => setAddOpen(true)} className="rounded-full bg-[#0A1F44] text-white">
          <Plus className="w-4 h-4 mr-1" /> Add user
        </Button>
      </div>

      {pending.length > 0 && ["admin", "platinum", "team_leader"].includes(currentUser?.role) && (
        <Card className="p-5 border-[#C9A961]/40 bg-[#FFFBEF]" data-testid="pending-panel">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <UserCheck className="w-4 h-4 text-[#8A6D1F]" />
              <h2 className="font-serif text-lg text-[#0A1F44]">Pending Access Requests</h2>
              <span data-testid="pending-count" className="text-[11px] font-semibold bg-[#C9A961] text-[#0A1F44] rounded-full px-2 py-0.5">
                {pending.length}
              </span>
            </div>
          </div>
          <div className="space-y-2" data-testid="pending-list">
            {pending.map((u) => (
              <div key={u.id} className="flex items-center justify-between gap-3 bg-white border border-[#C9A961]/30 rounded-lg p-3">
                <div className="flex items-center gap-3 min-w-0">
                  {u.picture
                    ? <img src={u.picture} alt="" className="w-9 h-9 rounded-full shrink-0" />
                    : <div className="w-9 h-9 rounded-full bg-slate-200 shrink-0" />}
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate" data-testid={`pending-name-${u.id}`}>{u.name}</div>
                    <div className="text-xs text-slate-500 truncate" data-testid={`pending-email-${u.id}`}>{u.email}</div>
                    <div className="text-[11px] mt-1 text-slate-500">
                      {u.upline_team || "no team"}
                      {u.nearest_upline_leader_id ? ` · upline: ${userMap[u.nearest_upline_leader_id]?.name || "—"}` : ""}
                      <span className="text-slate-400"> · requested {fmtDate(u.created_at)}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="sm" onClick={() => openApprove(u)} data-testid={`approve-open-${u.id}`}
                          className="bg-[#0A1F44] text-white hover:bg-[#0A1F44]/90">
                    <UserCheck className="w-4 h-4 mr-1" /> Review
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setConfirmReject(u)} data-testid={`reject-${u.id}`}
                          className="text-[#8A1F1F] hover:bg-[#FBECEC]">
                    <X className="w-4 h-4 mr-1" /> Decline
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="p-5">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-[#C9A961]" />
            <h2 className="font-serif text-lg text-[#0A1F44]">Users ({filtered.length})</h2>
          </div>
          <div className="flex items-center gap-2">
            <Select value={filter.role} onValueChange={(v) => setFilter({ ...filter, role: v })}>
              <SelectTrigger className="h-9 w-32" data-testid="filter-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All roles</SelectItem>
                {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filter.team} onValueChange={(v) => setFilter({ ...filter, team: v })}>
              <SelectTrigger className="h-9 w-36" data-testid="filter-team"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All teams</SelectItem>
                {ref?.teams.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="mb-3 space-y-1.5">
          <label className="relative block">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, couple name or email" aria-label="Search users"
                   className="h-11 pl-9 pr-10 text-base sm:text-sm" data-testid="admin-user-search" />
            {q && <button type="button" onClick={() => setQ("")} aria-label="Clear search" data-testid="admin-user-search-clear"
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded hover:bg-slate-100"><X className="w-4 h-4 text-slate-500" /></button>}
          </label>
          {q.trim() && <div className="text-xs text-slate-500" data-testid="admin-user-search-count">{searchHits ? `${filtered.length} match${filtered.length === 1 ? "" : "es"} for “${q.trim()}”` : "Searching…"}</div>}
          <label className="inline-flex items-center gap-2 text-xs text-slate-600 min-h-[36px]">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} data-testid="admin-show-archived" />
            Show archived ({archivedCount})
          </label>
        </div>

        <div className="space-y-2" data-testid="user-list">
          {loading && <div className="text-sm text-slate-500">Loading…</div>}
          {!loading && q.trim() && searchHits && !filtered.length && (
            <div className="text-sm text-slate-500 border border-dashed rounded-lg p-4" data-testid="admin-user-search-empty">
              No users match “{q.trim()}”{(filter.role !== "__all__" || filter.team !== "__all__") ? " with the current role/team filters" : ""}. Try part of a first name, last name or email.
            </div>
          )}
          {filtered.map((u) => (
            <div key={u.id} className="flex items-center justify-between gap-3 border border-slate-200 rounded-lg p-3">
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-900 truncate flex items-center gap-2">
                  {u.name}
                  <span className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">{u.role}</span>
                  {!u.active && <span className="text-[10px] uppercase tracking-wider bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">disabled</span>}
                  {u.is_placeholder && <span className="text-[10px] uppercase tracking-wider bg-[#F4EFE1] text-[#6B5A1D] px-1.5 py-0.5 rounded">placeholder</span>}
                  {!u.approved && <span className="text-[10px] uppercase tracking-wider bg-[#FBECEC] text-[#8A1F1F] px-1.5 py-0.5 rounded">pending</span>}
                  {u.platinum_admin && <span className="text-[10px] uppercase tracking-wider bg-[#0A1F44] text-[#C9A961] px-1.5 py-0.5 rounded" data-testid={`platinum-admin-badge-${u.id}`}>platinum admin</span>}
                </div>
                <div className="text-xs text-slate-500 truncate">{u.email}</div>
                <div className="text-[11px] mt-1 text-slate-500">
                  {u.upline_team || "no team"}
                  {u.nearest_upline_leader_id ? ` · upline: ${userMap[u.nearest_upline_leader_id]?.name || "—"}` : ""}
                  <span className="text-slate-400"> · joined {fmtDate(u.created_at)}</span>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {u.id !== currentUser?.id && u.active && u.approved && (
                  <Button
                    size="sm" variant="ghost"
                    onClick={async () => { await startViewAs(u.id); window.location.href = "/"; }}
                    data-testid={`view-as-${u.id}`}
                    title={`View as ${u.name}`}
                  >
                    <Eye className="w-4 h-4 text-[#0A1F44]" />
                  </Button>
                )}
                {canArchive(u) && !u.archived && (
                  <Button size="sm" variant="ghost" onClick={() => {
                    const reason = window.prompt(`Archive ${u.name}? Optional audit note:`, "") ?? null;
                    if (reason === null) return;
                    doArchive(u, reason);
                  }} data-testid={`archive-${u.id}`} title="Archive partner">
                    <PowerOff className="w-4 h-4 text-[#8A6D1F]" />
                  </Button>
                )}
                {canArchive(u) && u.archived && (
                  <Button size="sm" variant="ghost" onClick={() => doReactivate(u)} data-testid={`reactivate-${u.id}`} title="Reactivate partner">
                    <Power className="w-4 h-4 text-[#1F5D34]" />
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => setEditing(u)} data-testid={`edit-${u.id}`}>Edit</Button>
                {currentUser?.role === "admin" && u.role === "platinum" && !u.is_placeholder && !u.archived && (
                  <Button size="sm" variant="ghost" onClick={() => togglePlatinumAdmin(u)} data-testid={`platinum-admin-toggle-${u.id}`}
                          title={u.platinum_admin ? "Remove Platinum Admin" : "Make Platinum Admin"}>
                    <ShieldCheck className={`w-4 h-4 ${u.platinum_admin ? "text-[#C9A961]" : "text-slate-400"}`} />
                  </Button>
                )}
                {currentUser?.role === "admin" &&
                  u.role === "partner" && u.approved && u.active && !u.archived && (
                  <Button
                    size="sm" variant="ghost"
                    onClick={() => setPromoting({
                      id: u.id, name: u.name,
                      upline_team: u.upline_team,
                      upline_name: userMap[u.nearest_upline_leader_id]?.name,
                    })}
                    data-testid={`promote-${u.id}`}
                    title={`Promote ${u.name} to Leader`}
                  >
                    <UserCheck className="w-4 h-4 text-[#0A1F44]" />
                  </Button>
                )}
                <Button size="sm" variant="ghost" onClick={() => setPwUser(u)} data-testid={`pw-${u.id}`} title="Reset password">
                  <KeyRound className="w-4 h-4" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => toggle(u)} data-testid={`toggle-${u.id}`} title={u.active ? "Disable" : "Enable"}>
                  {u.active ? <PowerOff className="w-4 h-4 text-[#8A1F1F]" /> : <Power className="w-4 h-4 text-[#1F5D34]" />}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(u)} data-testid={`del-${u.id}`} title="Delete">
                  <Trash2 className="w-4 h-4 text-slate-500" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-5">
        <h2 className="font-serif text-lg text-[#0A1F44]">Demo data</h2>
        <p className="text-sm text-slate-500 mt-1">Demo PBOs are no longer seeded. Use the one-time clean-launch reset to clear test activity.</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <Button asChild variant="outline" data-testid="launch-reset-link"><Link to="/admin/launch-reset">Clean-launch activity reset</Link></Button>
          <Button data-testid="wipe-demo-btn" variant="outline" onClick={() => setConfirmWipe(true)} className="text-[#8A1F1F] border-[#8A1F1F]/30 hover:bg-[#FBECEC]">
            <Trash2 className="w-4 h-4 mr-1" /> Remove all demo PBOs
          </Button>
        </div>
      </Card>

      {/* Add user */}
      <UserForm
        open={addOpen}
        onOpenChange={setAddOpen}
        onSaved={load}
        ref_={ref}
        leaderOptions={leaderOptions}
        userMap={userMap}
      />

      {/* Edit user */}
      {editing && (
        <UserForm
          open={!!editing}
          onOpenChange={(o) => { if (!o) setEditing(null); }}
          user={editing}
          onSaved={() => { setEditing(null); load(); }}
          ref_={ref}
          leaderOptions={leaderOptions}
          userMap={userMap}
        />
      )}

      {/* Reset PW */}
      <Dialog open={!!pwUser} onOpenChange={(o) => { if (!o) { setPwUser(null); setPwVal(""); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-serif text-[#0A1F44]">Reset password{pwUser ? ` — ${pwUser.name}` : ""}</DialogTitle></DialogHeader>
          <div>
            <Label>New password</Label>
            <Input data-testid="pw-input" type="text" value={pwVal} onChange={(e) => setPwVal(e.target.value)} />
            <div className="text-xs text-slate-500 mt-1">Share this securely.</div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setPwUser(null); setPwVal(""); }}>Cancel</Button>
            <Button data-testid="pw-save" onClick={changePw} className="bg-[#0A1F44] text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmWipe} onOpenChange={setConfirmWipe}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove all demo PBOs?</AlertDialogTitle>
            <AlertDialogDescription>Only records flagged as demo will be deleted.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="wipe-confirm" onClick={wipeDemo} className="bg-[#8A1F1F] hover:bg-[#701818]">Delete demo</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => { if (!o) setConfirmDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete user {confirmDelete?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              <OwnedPBOsPanel user={confirmDelete} onChanged={load} />
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="user-delete-confirm" className="bg-[#8A1F1F] hover:bg-[#701818]">Delete user</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Approve pending signup */}
      <Dialog open={!!approving} onOpenChange={(o) => { if (!o) { setApproving(null); setApproveTeam(""); setApproveLeader(""); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-serif text-[#0A1F44]">
              Review access request {approving ? `— ${approving.name}` : ""}
            </DialogTitle>
            <DialogDescription>
              Verified Google email: <span className="font-medium text-slate-800">{approving?.email}</span>. Confirm or correct the team &amp; upline before approving. Role stays Partner; only Sean &amp; Rina can promote later.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Team</Label>
              <Select value={approveTeam || "__none__"} onValueChange={(v) => { setApproveTeam(v === "__none__" ? "" : v); setApproveLeader(""); }}>
                <SelectTrigger data-testid="approve-team"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— select team —</SelectItem>
                  {ref?.teams.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Nearest upline leader</Label>
              <Select value={approveLeader || "__none__"} onValueChange={(v) => setApproveLeader(v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid="approve-leader"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— none —</SelectItem>
                  {approveLeadersFiltered.filter((l) => l.id !== approving?.id).map((l) => (
                    <SelectItem key={l.id} value={l.id}>
                      {l.name} · {l.role}{l.upline_team ? ` · ${l.upline_team}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setApproving(null)}>Cancel</Button>
            <Button data-testid="approve-submit" onClick={doApprove} className="bg-[#0A1F44] text-white">
              <UserCheck className="w-4 h-4 mr-1" /> Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmReject} onOpenChange={(o) => { if (!o) setConfirmReject(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Decline access request?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmReject?.name} ({confirmReject?.email}) will be blocked from signing in. They cannot resubmit without your invitation.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doReject} data-testid="reject-confirm" className="bg-[#8A1F1F] hover:bg-[#701818]">Decline</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {currentUser?.role === "admin" && <PboLinkPanel />}
      {currentUser?.can_sync_content && <ContentSyncPanel />}
      {currentUser?.can_sync_content && <BackupPanel />}

      <PromoteLeaderDialog
        open={!!promoting}
        target={promoting}
        onClose={() => setPromoting(null)}
        onDone={load}
      />
    </div>
  );
}

function UserForm({ open, onOpenChange, user, onSaved, ref_, leaderOptions, userMap }) {
  const isEdit = !!user;
  const [form, setForm] = useState({
    name: user?.name || "",
    email: user?.email || "",
    role: user?.role || "partner",
    upline_team: user?.upline_team || "",
    nearest_upline_leader_id: user?.nearest_upline_leader_id || "",
    approved: user ? !!user.approved : true,
    active: user ? !!user.active : true,
    password: "",
  });
  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "",
        email: user.email || "",
        role: user.role,
        upline_team: user.upline_team || "",
        nearest_upline_leader_id: user.nearest_upline_leader_id || "",
        approved: !!user.approved, active: !!user.active,
        password: "",
      });
    } else {
      setForm({
        name: "", email: "", role: "partner", upline_team: "", nearest_upline_leader_id: "",
        approved: true, active: true, password: "",
      });
    }
  }, [user, open]);

  const availableLeaders = useMemo(() => {
    if (!form.upline_team) return leaderOptions;
    return leaderOptions.filter((l) => l.upline_team === form.upline_team || l.role === "admin");
  }, [form.upline_team, leaderOptions]);

  const submit = async () => {
    try {
      if (isEdit) {
        const body = { ...form };
        if (!body.password) delete body.password;
        body.nearest_upline_leader_id = body.nearest_upline_leader_id || null;
        body.upline_team = body.upline_team || null;
        await api.patch(`/users/${user.id}`, body);
      } else {
        if (!form.email || !form.name) { toast.error("Name and email required"); return; }
        await api.post("/users", {
          name: form.name, email: form.email, role: form.role,
          upline_team: form.upline_team || null,
          nearest_upline_leader_id: form.nearest_upline_leader_id || null,
          approved: form.approved,
          password: form.password || null,
          onboarded: !!form.upline_team,
        });
      }
      toast.success(isEdit ? "Updated" : "Created");
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-serif text-[#0A1F44]">{isEdit ? `Edit ${user.name}` : "Add user"}</DialogTitle>
          <DialogDescription>Set the role and hierarchy so this account sees the correct scope.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Name</Label><Input data-testid="uf-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Email</Label><Input data-testid="uf-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="uf-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Upline team</Label>
              <Select value={form.upline_team || "__none__"} onValueChange={(v) => setForm({ ...form, upline_team: v === "__none__" ? "" : v, nearest_upline_leader_id: "" })}>
                <SelectTrigger data-testid="uf-team"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— none —</SelectItem>
                  {ref_?.teams.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Nearest upline leader</Label>
            <Select value={form.nearest_upline_leader_id || "__none__"} onValueChange={(v) => setForm({ ...form, nearest_upline_leader_id: v === "__none__" ? "" : v })}>
              <SelectTrigger data-testid="uf-upline"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— none (top level) —</SelectItem>
                {availableLeaders.filter((l) => l.id !== user?.id).map((l) => (
                  <SelectItem key={l.id} value={l.id}>{l.name} · {l.role}{l.upline_team ? ` · ${l.upline_team}` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.approved} onChange={(e) => setForm({ ...form, approved: e.target.checked })} data-testid="uf-approved" /> Approved
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="uf-active" /> Active
            </label>
          </div>
          <div>
            <Label>{isEdit ? "New password (optional)" : "Temp password (optional)"}</Label>
            <Input data-testid="uf-password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </div>

          {isEdit && user && user.role !== "admin" && (
            <DangerZone user={user} onDone={() => { onSaved?.(); onOpenChange(false); }} />
          )}

          {isEdit && user?.is_placeholder && (
            <GoogleLinkPanel user={user} onLinked={() => { onSaved?.(); onOpenChange(false); }} />
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="uf-submit" onClick={submit} className="bg-[#0A1F44] text-white">{isEdit ? "Save" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OwnedPBOsPanel({ user, onChanged }) {
  const [pbos, setPbos] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [confirmPboId, setConfirmPboId] = useState(null);
  const [typed, setTyped] = useState("");

  const load = async () => {
    if (!user?.id) return;
    try {
      const { data } = await api.get("/pbos", { params: { scope: "all", ibo_partner_id: user.id, include_demo: true } });
      setPbos(data || []);
    } catch { setPbos([]); }
  };

  useEffect(() => { setPbos(null); setConfirmPboId(null); setTyped(""); load(); /* eslint-disable-next-line */ }, [user?.id]);

  if (!user) return null;
  if (pbos === null) return <span className="text-xs text-slate-500">Checking dependencies…</span>;

  if (pbos.length === 0) {
    return (
      <span className="text-slate-700 text-sm">
        {user.name} owns no PBOs. Deleting removes their profile only — their Google email is fully released and can be approved again later.
      </span>
    );
  }

  const doDelete = async (pboId) => {
    if (typed !== "DELETE") { toast.error('Type DELETE to confirm'); return; }
    setBusyId(pboId);
    try {
      await api.delete(`/pbos/${pboId}`);
      toast.success("PBO permanently deleted");
      setConfirmPboId(null); setTyped("");
      await load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusyId(null); }
  };

  return (
    <div className="space-y-2" data-testid="owned-pbos-panel">
      <div className="text-slate-700 text-sm">
        {user.name} still owns <span className="font-medium">{pbos.length}</span> PBO{pbos.length === 1 ? "" : "s"}. Deletion is blocked until they own none. Permanently delete test/mistake PBOs below, or reassign real ones via their owner detail page first.
      </div>
      <div className="max-h-60 overflow-y-auto rounded-md border border-slate-200 divide-y divide-slate-100">
        {pbos.map((p) => (
          <div key={p.id} className="p-2 flex items-center justify-between gap-2">
            <div className="min-w-0">
              <div className="text-sm text-slate-900 truncate">
                {p.name} <span className="text-slate-400 text-xs">· {p.current_stage} · {p.status}</span>
              </div>
              <div className="text-[11px] text-slate-500 truncate">{p.upline_team || "—"}</div>
            </div>
            {confirmPboId === p.id ? (
              <div className="flex items-center gap-1 shrink-0">
                <Input
                  data-testid={`pbo-del-input-${p.id}`}
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  placeholder="DELETE"
                  className="h-8 w-24 text-xs"
                  autoComplete="off"
                />
                <Button size="sm" variant="ghost" onClick={() => { setConfirmPboId(null); setTyped(""); }} className="h-8">Cancel</Button>
                <Button
                  size="sm"
                  data-testid={`pbo-del-confirm-${p.id}`}
                  disabled={busyId === p.id}
                  onClick={() => doDelete(p.id)}
                  className="h-8 bg-[#8A1F1F] text-white hover:bg-[#701818]"
                >
                  {busyId === p.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Delete"}
                </Button>
              </div>
            ) : (
              <Button
                size="sm"
                variant="outline"
                data-testid={`pbo-del-${p.id}`}
                onClick={() => { setConfirmPboId(p.id); setTyped(""); }}
                className="h-8 border-[#8A1F1F]/40 text-[#8A1F1F] hover:bg-[#FBECEC] shrink-0"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
              </Button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function GoogleLinkPanel({ user, onLinked }) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const value = (email || "").trim().toLowerCase();
    if (!value.includes("@")) { toast.error("Enter a valid Google email"); return; }
    setBusy(true);
    try {
      await api.post(`/users/${user.id}/link-google-email`, { email: value });
      toast.success(`${value} linked to ${user.name}`);
      onLinked?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-4 border border-[#C9A961]/40 bg-[#FFFBEF] rounded-lg p-3" data-testid="google-link-panel">
      <div className="text-[11px] uppercase tracking-[0.24em] text-[#8A6D1F] font-semibold">Activate Google login</div>
      <div className="text-xs text-slate-600 mt-1">
        Bind ONE real Google email to <span className="font-medium">{user.name}</span>. Existing role, team, upline and history are preserved — no duplicate account is created when they Sign in with Google.
      </div>
      <div className="mt-3 flex items-center gap-2">
        <Input
          data-testid="google-link-email"
          type="email"
          placeholder="name@gmail.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="h-9"
        />
        <Button
          data-testid="google-link-submit"
          onClick={submit}
          disabled={busy}
          className="h-9 bg-[#0A1F44] text-white"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Link Google login"}
        </Button>
      </div>
    </div>
  );
}

function DangerZone({ user, onDone }) {
  const [preview, setPreview] = useState(null);
  const [mode, setMode] = useState(null);   // "mark" | "reset" | null
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/users/${user.id}/test-preview`).then((r) => { if (alive) setPreview(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, [user.id]);

  const openMark = () => { setMode("mark"); setTyped(""); };
  const openReset = () => { setMode("reset"); setTyped(""); };
  const cancel = () => { setMode(null); setTyped(""); };

  const confirmMark = async () => {
    if (typed !== "MARK TEST") { toast.error('Type MARK TEST to confirm'); return; }
    setBusy(true);
    try {
      await api.post(`/users/${user.id}/mark-test`, { confirm: "MARK TEST" });
      toast.success(`${user.name} marked as Test Account`);
      cancel(); onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const confirmReset = async () => {
    if (typed !== "RESET") { toast.error('Type RESET to confirm'); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/users/${user.id}/reset-test-account`, { confirm: "RESET" });
      toast.success(`Reset complete — ${data.pbos_deleted} test PBO(s) & ${data.sessions_deleted} session(s) removed`);
      cancel(); onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!preview) return null;
  if (preview.protected) return null;   // never render for admin/protected

  return (
    <div className="mt-4 border border-[#8A1F1F]/30 bg-[#FBECEC]/40 rounded-lg p-3" data-testid="danger-zone">
      <div className="text-[11px] uppercase tracking-[0.24em] text-[#8A1F1F] font-semibold">Danger Zone</div>
      <div className="text-xs text-slate-600 mt-1">
        {preview.pbo_count} PBO{preview.pbo_count === 1 ? "" : "s"} · {preview.test_pbo_count} flagged test · {preview.session_count} active session{preview.session_count === 1 ? "" : "s"}
        {preview.is_test && <span className="ml-2 text-[10px] uppercase tracking-wider bg-[#F4EFE1] text-[#6B5A1D] px-1.5 py-0.5 rounded">test account</span>}
      </div>

      {mode === null && (
        <div className="mt-2 flex flex-wrap gap-2">
          {!preview.is_test && (
            <Button size="sm" variant="outline" onClick={openMark} data-testid="mark-test-btn"
                    className="text-[#8A6D1F] border-[#C9A961]/50 hover:bg-[#FFFBEF]">
              Mark as Test Account
            </Button>
          )}
          {preview.is_test && (
            <Button size="sm" onClick={openReset} data-testid="reset-test-btn"
                    className="bg-[#8A1F1F] hover:bg-[#701818] text-white">
              Reset Test Account
            </Button>
          )}
        </div>
      )}

      {mode === "mark" && (
        <div className="mt-3 space-y-2">
          <div className="text-xs text-slate-700">
            Flag <span className="font-medium">{user.name}</span> ({user.email}) and their {preview.pbo_count} PBO(s) as test data.
            Type <span className="font-mono font-bold">MARK TEST</span> to confirm.
          </div>
          <Input data-testid="mark-input" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="MARK TEST" />
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="ghost" onClick={cancel}>Cancel</Button>
            <Button size="sm" data-testid="mark-confirm" onClick={confirmMark} disabled={busy}
                    className="bg-[#8A6D1F] text-white hover:bg-[#6B5A1D]">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Mark as test"}
            </Button>
          </div>
        </div>
      )}

      {mode === "reset" && (
        <div className="mt-3 space-y-2">
          <div className="text-xs text-slate-700">
            This will permanently delete <span className="font-medium">{user.name}</span> ({user.email}), <span className="font-medium">{preview.test_pbo_count}</span> test PBO(s), all appointments/history, and <span className="font-medium">{preview.session_count}</span> active session(s). The email address will be released and treated as a brand-new user on next Google sign-in.
            <br />Type <span className="font-mono font-bold">RESET</span> to confirm.
          </div>
          <Input data-testid="reset-input" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="RESET" />
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="ghost" onClick={cancel}>Cancel</Button>
            <Button size="sm" data-testid="reset-confirm" onClick={confirmReset} disabled={busy}
                    className="bg-[#8A1F1F] hover:bg-[#701818] text-white">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reset test account"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
